// src/features/locker-assignments/components/LockerAssignmentCreate.tsx
import React from 'react';
import { Create, SimpleForm, useRedirect, useNotify } from 'react-admin';
import { Grid } from '@mui/material';

import { MyToolbar, MyFormContainer } from '@/components';
import { useLockerAssignmentForm } from '../hooks/useLockerAssignmentForm';
import { FormValidationHandler } from './forms/formValidation';
import { HotelSelectionSection } from './forms/HotelSelectionSection';
import { AssignmentDetailsSection } from './forms/AssignmentDetailsSection';
import { LockerRequirementsSection } from './forms/LockerRequirementsSection';
import { ZonesSection } from './forms/ZonesSection';
import { NotesSection } from './forms/NotesSection';

const LockerAssignmentCreate: React.FC = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  
  const {
    // Estado
    selectedHotel,
    hotels,
    zones,
    referenceFields,
    lockerRequirements,
    selectedZones,
    referenceValues,
    loadingHotelData,
    loadingHotels,
    validationErrors,
    
    // Handlers
    handleHotelChange,
    handleLockerRequirementChange,
    handleReferenceValueChange,
    handleZoneChange,
    handleCancel,
    setValidationErrors,
    
    // Funciones de utilidad
    validateForm,
    handleSubmit,
    
    // Valores computados
    defaultValues,
  } = useLockerAssignmentForm();

  // Handler personalizado para el éxito que redirige al listado con filtro de pendientes
  const handleSuccess = (data: any) => {
    notify('resources.locker-assignments.notifications.created', { 
      type: 'success',
      messageArgs: { 
        smart: false,
        _: 'Assignment created successfully. You can now add tags from the list.' 
      }
    });
    
    // Redirigir al listado con el filtro de asignaciones pendientes
    const queryParams = new URLSearchParams();
    queryParams.set('filter', JSON.stringify({ status: 'Pending', hotelId: selectedHotel?.id }));

    redirect(`/locker-assignments?${queryParams.toString()}`);
  };

  return (
    <Create 
      title="resources.locker-assignments.titles.create"
      redirect={false} // Manejamos la redirección manualmente
      mutationOptions={{
        onSuccess: handleSuccess
      }}
      transform={handleSubmit}
    >
      <MyFormContainer>
        <SimpleForm
          toolbar={
            <MyToolbar 
              onCancel={handleCancel} 
              resource="locker-assignments" 
              showDelete={false}
            />
          }
          validate={validateForm}
          defaultValues={defaultValues}
        >
          {/* Componente de validación del formulario */}
          <FormValidationHandler 
            selectedHotel={selectedHotel}
            lockerRequirements={lockerRequirements}
            referenceFields={referenceFields}
            referenceValues={referenceValues}
          />
          
          {/* Sección de selección de hotel y campos de referencia */}
          <HotelSelectionSection
            selectedHotel={selectedHotel}
            hotels={hotels}
            loadingHotels={loadingHotels}
            referenceFields={referenceFields}
            referenceValues={referenceValues}
            onHotelChange={handleHotelChange}
            onReferenceValueChange={handleReferenceValueChange}
          />

          {selectedHotel && (
            <>
              {/* Solo sección de detalles de asignación (fechas) */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <AssignmentDetailsSection />
                </Grid>
              </Grid>

              {/* Sección de requerimientos de taquillas, zonas y notas */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}> 
                  <LockerRequirementsSection
                    lockerRequirements={lockerRequirements}
                    loadingHotelData={loadingHotelData}
                    validationErrors={validationErrors}
                    onLockerRequirementChange={handleLockerRequirementChange}
                    setValidationErrors={setValidationErrors}
                  />
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <ZonesSection
                    zones={zones}
                    selectedZones={selectedZones}
                    loadingHotelData={loadingHotelData}
                    onZoneChange={handleZoneChange}
                  />
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <NotesSection />
                </Grid>
              </Grid>
            </>
          )}
        </SimpleForm>
      </MyFormContainer>
    </Create>
  );
};

export default LockerAssignmentCreate;